import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { captureAttribution } from "@/lib/attribution";
import { sendReplayEvent } from "@/lib/replayTracking";
import { REPLAY_TICKER_ITEMS, REPLAY_DISCLOSURE } from "@/lib/replay";
import { Nav } from "@/components/Nav";
import { Ticker } from "@/components/Ticker";
import { MobileCTA } from "@/components/MobileCTA";
import { Footer } from "@/sections/Footer";
import { ReplayHero } from "@/sections/replay/ReplayHero";
import { ReplayRecap, ReplayMethod, ReplaySession, ReplayPromise, ReplayObjections, ReplayProof } from "@/sections/replay/ReplayContent";
import { ReplayHost } from "@/sections/replay/ReplayHost";
import { ReplayFAQ, ReplayBook } from "@/sections/replay/ReplayBook";

gsap.registerPlugin(ScrollTrigger);

const NAV_LINKS = [
  { label: "WATCH", id: "watch" },
  { label: "RECAP", id: "recap" },
  { label: "THE METHOD", id: "method" },
  { label: "HOST", id: "host" },
  { label: "FAQ", id: "faq" },
];

export default function Replay() {
  const heroRef = useRef(null);

  useEffect(() => {
    captureAttribution();
    // engaged-view signal after ten seconds on page (not on load)
    const t = setTimeout(() => sendReplayEvent("replay_viewed"), 10000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="grain" data-testid="replay-page">
      <a
        href="#book"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-[var(--accent)] focus:text-[var(--background-deep)] focus:px-4 focus:py-2 focus:rounded-[4px]"
      >
        Skip to booking
      </a>
      <Nav links={NAV_LINKS} ctaLabel="REQUEST MY BENEFIT-TO-KEYS SESSION ↗" ctaTarget="book" />
      <main>
        <ReplayHero ref={heroRef} />
        <Ticker items={REPLAY_TICKER_ITEMS} />
        <ReplayRecap />
        <ReplayMethod />
        <ReplaySession />
        <ReplayPromise />
        <ReplayHost />
        <ReplayObjections />
        <ReplayProof />
        <ReplayFAQ />
        <ReplayBook />
      </main>
      <Footer disclosure={REPLAY_DISCLOSURE} />
      <MobileCTA
        heroFormId="watch"
        label="REQUEST MY BENEFIT-TO-KEYS SESSION"
        targetId="book"
        onClick={() => sendReplayEvent("cta_clicked")}
      />
    </div>
  );
}
